import { useState, useEffect, useCallback, useRef } from 'react';
import type { Order } from '@/lib/supabase';

export type NotifPermission = 'default' | 'granted' | 'denied' | 'unsupported';

// ── Tell SW that an order alarm should be stopped ──
export function notifySWOrderActionTaken(orderId: string): void {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
  navigator.serviceWorker.controller.postMessage({
    type: 'ORDER_ACTION_TAKEN',
    orderId,
  });
}

// ── Tell SW to stop ALL alarms ──
export function notifySWStopAllAlarms(): void {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
  navigator.serviceWorker.controller.postMessage({ type: 'STOP_ALL_ALARMS' });
}

// ── Tell SW about pending orders on page load ──
export function notifySWPendingOrders(orders: Order[]): void {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
  navigator.serviceWorker.controller.postMessage({
    type: 'PENDING_ORDERS_ON_LOAD',
    orders,
  });
}

export function usePushNotification(onRingNow?: (order: Order) => void) {
  const [permission, setPermission] = useState<NotifPermission>('default');
  const [swReady, setSwReady] = useState(false);
  const onRingNowRef = useRef(onRingNow);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    onRingNowRef.current = onRingNow;
  }, [onRingNow]);

  // Register SW + listen for messages from SW
  useEffect(() => {
    if (!('Notification' in window)) {
      setPermission('unsupported');
      return;
    }
    setPermission(Notification.permission as NotifPermission);

    if (!('serviceWorker' in navigator)) return;

    const registerSW = async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          updateViaCache: 'none',
        });

        registrationRef.current = reg;
        setSwReady(true);

        // Force update
        await reg.update();

        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'activated') {
                setSwReady(true);
              }
            });
          }
        });
      } catch (err) {
        console.warn('SW registration failed:', err);
      }
    };

    registerSW();

    // Listen for messages FROM service worker
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'RING_NOW') {
        if (onRingNowRef.current) {
          onRingNowRef.current(event.data.order);
        }
      }
      if (event.data?.type === 'ACCEPT_ORDER') {
        // Notification Accept button tapped — reload to refresh state
        window.location.reload();
      }
      if (event.data?.type === 'REJECT_ORDER') {
        window.location.reload();
      }
    };

    navigator.serviceWorker.addEventListener('message', handleSWMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleSWMessage);
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!('Notification' in window)) return false;
    try {
      const result = await Notification.requestPermission();
      setPermission(result as NotifPermission);
      return result === 'granted';
    } catch {
      return false;
    }
  }, []);

  // Send new order to SW — SW handles the persistent alarm
  const sendNewOrderNotification = useCallback(async (order: Order) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;

    try {
      if ('serviceWorker' in navigator) {
        // Wait for SW to be ready
        const reg = await navigator.serviceWorker.ready;

        if (navigator.serviceWorker.controller) {
          // Send to SW — SW starts persistent alarm
          navigator.serviceWorker.controller.postMessage({
            type: 'NEW_ORDER',
            order,
          });
        } else {
          // Fallback: show directly
          await reg.showNotification('🔔 New Order!', {
            body: `👤 ${order.customer_name}  •  ₹${order.final_amount}  •  ${order.customer_city}`,
            icon: '/vite.svg',
            badge: '/vite.svg',
            vibrate: [1000, 300, 1000, 300, 1000],
            tag: `order-${order.id}`,
            renotify: true,
            requireInteraction: true,
            silent: false,
            data: { url: '/admin', orderId: order.id },
          });
        }
      }
    } catch (err) {
      console.warn('Push notification error:', err);
    }
  }, []);

  // Keep SW alive
  useEffect(() => {
    if (!swReady) return;
    const keepAlive = setInterval(() => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'KEEP_ALIVE' });
      }
    }, 20000);
    return () => clearInterval(keepAlive);
  }, [swReady]);

  return { permission, swReady, requestPermission, sendNewOrderNotification };
}
